import React, { useState } from 'react';
import Modal from '@/components/common/Modal/Modal';
import Button from '@/components/common/Button/Button';
import { activateGiftCard } from '@/services/giftCard/giftCardService';
import { useToast } from '@/hooks/useToast';
import styles from './GiftCardActivationModal.module.css';

/**
 * GiftCardActivationModal Component
 * Modal for activating gift cards by entering a code
 */
const GiftCardActivationModal = ({ isOpen, onClose, onActivate }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const { showSuccess, showError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!code.trim()) {
      setError('Please enter a gift card code');
      return;
    }

    setIsActivating(true);
    
    try {
      const response = await activateGiftCard(code.trim());
      
      if (response.success) {
        showSuccess('Gift card activated successfully!');
        if (onActivate) {
          onActivate(code.trim().toUpperCase());
        }
        setCode('');
        onClose();
      } else {
        setError(response.message || 'Failed to activate gift card');
        showError(response.message || 'Failed to activate gift card');
      }
    } catch (error) {
      // Extract error message from API response
      let errorMessage = 'Failed to activate gift card. Please try again.';
      
      if (error.response?.data) {
        // Check for error field first (e.g., {"error":"Gift card not activated by user"})
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } 
        // Check for message field
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        // Check for error in nested structure
        else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsActivating(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Activate Gift Card"
      size="lg"
      bgColor="linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%) !important;"
      className={styles.activationModal}
    >
      <div className={styles.modalContent}>
        {/* Left Section - Form */}
        <div className={styles.leftSection}>
          <div className={styles.contentWrapper}>
            <h2 className={styles.mainTitle}>Activate awesome deals!</h2>
            <p className={styles.description}>
              Enter your gift card code to activate and start tracking your wager requirements.
            </p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputGroup}>
                <label htmlFor="giftCardCode" className={styles.inputLabel}>
                  Enter Gift Card Code
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    id="giftCardCode"
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      setError('');
                    }}
                    placeholder="Enter a code"
                    className={styles.input}
                    autoFocus
                    disabled={isActivating}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isActivating || !code.trim()}
                    className={styles.activateButton}
                  >
                    {isActivating ? 'Activating...' : 'Activate'}
                  </Button>
                </div>
                {error && <p className={styles.error}>{error}</p>}
              </div>
            </form>
          </div>
        </div>

        {/* Right Section - Image */}
        <div className={styles.rightSection}>
          <img
            src="/assets/images/vocuher.webp"
            alt="Gift Card Voucher"
            className={styles.voucherImage}
            loading="lazy"
          />
        </div>
      </div>
    </Modal>
  );
};

export default GiftCardActivationModal;
